import { isArray, includes, remove } from 'lodash-es';

import * as AssetActions from '../../state/assets/assets.action';
import * as SearchActions from '../../state/search/search.action';
import * as TableColumnActions from '../../state/table-columns/table-columns.action';
import template from './asset-list.component.html';

export class AssetListController {
  constructor($ngRedux, $filter, equipmentUpdateService) {
    'ngInject';

    this.selectMode = 'multi';
    this.allSelected = false;
    this.store = $ngRedux;
    this.SearchActions = SearchActions;
    this.AssetActions = AssetActions;
    this.TableColumnActions = TableColumnActions;
    this.$filter = $filter;
    this.visibleAssets = [];
    this.goToAssetTimeout = null;
    this.loading = true;
    this.equipmentUpdateService = equipmentUpdateService;
    this.lastCheckIndex = -1;

    this.mapStateToThis = this.mapStateToThis.bind(this);
  }

  $onInit() {
    this.unsubscribe = this.store.connect(this.mapStateToThis, {
      ...AssetActions,
      ...SearchActions,
      ...TableColumnActions,
    })(this);

    this.defaultBindings();
  }

  $onDestroy() {
    this.unsubscribe();
  }

  $postLink() {}

  /**
   * Perform change detection on one-way bindings.
   * @param {any} change
   */
  $onChanges() {}

  /**
   * Map application state values to component properties
   * @param {*} state Application state
   */
  mapStateToThis(state) {
    const { search, assets, tableColumns, security, user } = state;
    const { list = [] } = assets;
    const { accountNameLookup } = user;
    this.allSelected = list.length > 0 ? list.every((asset) => asset.selected) : false;
    return {
      loading: search.inProgress,
      securityGroups: security.groups,
      assets: list,
      pageSize: 50,
      totalCount: assets.totalCount,
      currentPage: assets.currentPage,
      totalPages: assets.totalPages,
      initialSearch: assets.initial,
      errorText: search.error,
      errorFlag: !(search.error === ''),
      last_viewed_id: assets.viewed_asset_id,
      columns: tableColumns.selected,
      sort: search.sort,
      lastViewedId: assets.viewedAssetId,
      accountsLookup: accountNameLookup || {},
    };
  }

  /**
   * Set all bindings to their friendly defaults if they have not been supplied by parent (yet).
   */
  defaultBindings() {
    if (this.assets == null) {
      this.assets = [];
    }
    if (this.selectMode == null) {
      this.selectMode = 'multi';
    }
    if (this.preSelectedIds == null) {
      this.preSelectedIds = [];
    }
    if (this.assetDetails == null) {
      this.assetDetails = () => null;
    }
    if (this.selectionChanged == null) {
      this.selectionChanged = () => null;
    }
  }

  toggleOrderBy(column) {
    this.updateSort(column.sort_key);
  }

  toggleShowAsset(asset) {
    if (includes(this.visibleAssets, asset.id)) {
      this.visibleAssets = remove(this.visibleAssets, asset.id);
    } else {
      this.visibleAssets.push(asset.id);
    }
  }

  visibleAsset(asset) {
    return includes(this.visibleAssets, asset.id);
  }

  /**
   * Propagate selection changes to parent.
   */
  updateSelections(event, index) {
    //event.stopPropagation();
    if (this.selectMode && this.assets.length > 0) {
      let currentSelections = [];
      if (this.selectMode === 'multi') {
        if (index > -1 && this.assets[index].selected) {
          if (event && event.shiftKey && this.lastCheckIndex !== -1) {
            // do range select
            const [start, end] =
              this.lastCheckIndex > index
                ? [index, this.lastCheckIndex]
                : [this.lastCheckIndex, index];
            this.assets.forEach((asset, i) => {
              // set the range to true
              if (i >= start && i <= end) asset.selected = true;
            });
          }
          this.lastCheckIndex = index;
        }
        currentSelections = this.assets.filter((item) => item.selected);
      } else {
        // don't fire callback if the selectMode is 'none' or not supported.
        return;
      }
      // dispatch action
      this.select(currentSelections);
    }
  }

  /**
   * Decide whether to display equipment status based on asset properties.
   * @param {*} asset
   */
  showStatus(asset) {
    return (
      asset &&
      asset.properties &&
      isArray(asset.properties) &&
      (asset.properties.includes('alert:high') ||
        asset.properties.includes('alert:medium') ||
        asset.properties.includes('alert:low') ||
        asset.properties.includes('maintenance:upcoming') ||
        asset.properties.includes('maintenance:due') ||
        asset.properties.includes('sharestatus:inbound') ||
        asset.properties.includes('sharestatus:outbound'))
    );
  }

  getOrderClass(column) {
    const selected = this.sort.name === column.sort_key;
    const activeClass = selected ? 'active' : '';
    const direction = !selected ? 'asc' : this.sort.direction;

    const arrowClass = direction === 'asc' ? 'arrow-up' : 'arrow-down';

    return `fa fa-${arrowClass} ${activeClass}`;
  }

  deSelecteLastViewedAsset() {
    this.resetLastViewedAsset();
  }

  /**
   * Propagate asset row clicks up to parent
   * @param {*} asset
   */
  assetClicked(asset) {
    this.viewed(asset);
    this.assetDetails({
      asset,
    });
  }

  /**
   * Returns a boolean flag indicating whether asset rows should be shown on the UI
   */
  shouldDisplayAssets() {
    return this.assets && this.assets.length > 0;
  }

  /**
   * Returns a boolean flag indicating whether failure/no results should be shown on the UI.
   */
  shouldDisplayFailure() {
    return !this.loading && (!this.assets || this.assets.length === 0);
  }

  selectAll() {
    this.allSelected = !this.allSelected;
    this.assets.forEach((asset) => (asset.selected = this.allSelected));
    const selections = this.assets.filter((item) => item.selected);
    this.select(selections);
    // GTM UPDATE -> GTM UPDATED
    window.dataLayer.push({
      event: this.allSelected ? 'selectAllAssets' : 'unselectAllAssets',
    });
  }

  // GTM UPDATE -> GTM UPDATED
  singleSelected(asset) {
    window.dataLayer.push({
      event: asset.selected ? 'singleUnselect' : 'singleSelect',
      data: {
        id: asset.id || 'unknown',
        name: asset.name || 'unknown',
      },
    });
  }

  bulbNotice() {
    window.dataLayer.push({
      event: 'bulbNoticeOpen',
    });

    this.notice = true;
  }

  hasMoreResults() {
    return this.currentPage < this.totalPages;
  }

  loadMore() {
    if (!this.loading && this.hasMoreResults()) {
      this.store.dispatch(this.AssetActions.selectPage(this.currentPage + 1));
    }
  }

  getLastSeenColorClass(date) {
    if (date === '' || date === undefined) {
      return '';
    }

    const now = moment(new Date());
    const lsn = moment(date);
    const days = moment.duration(now.diff(lsn)).asDays();

    switch (true) {
      case 0 < days && days < 1:
        return 'seen-24h';
      case 1 < days && days < 3:
        return 'seen-1-3d';
      case 3 < days && days < 10:
        return 'seen-3-10d';
      case 10 < days && days < 30:
        return 'seen-in-10-30d';
      default:
        return 'not-seen-30d';
    }
  }

  formatDateTime(dateTime) {
    let timeZone = '';
    let formattedDateTime = '';
    if (dateTime) {
      timeZone = moment.tz.guess();
      formattedDateTime = moment.tz(dateTime, timeZone).format('MM/DD/YYYY hh:mm:ss A');
    }
    return formattedDateTime;
  }

  openUpdateAsset(asset) {
    // GTM
    window.dataLayer.push({
      event: 'assetList-SingleAssetUpdateOpen',
      data: {
        id: asset.id || 'unknown',
        name: asset.name || 'unknown',
      },
    });
    this.equipmentUpdateService.openEditModal(asset).catch(() => {
      window.dataLayer.push({
        event: 'assetList-SingleAssetUpdateDismiss',
        data: {
          id: asset.id || 'unknown',
          name: asset.name || 'unknown',
        },
      });
    });
  }
}

export const assetListComponent = {
  template,
  bindings: {
    assetDetails: '&',
  },
  controller: AssetListController,
  controllerAs: 'vm',
};
